import * as React from "react";
import { useNavigate } from "react-router";

import { useAuth } from "@/providers";
import { type AgentRecord } from "@/shared/domain/agents/AgentRecord";
import { useAgents } from "@/shared/domain/agents/hooks";
import {
  LAUNCH_SUBJECT_KINDS,
  type LaunchSubject,
} from "@/shared/domain/agents/launchSubject";
import { Button, Popover } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import styles from "./LaunchAgentMenu.module.css";

interface LaunchAgentMenuProps {
  /**
   * The record to launch an agent on. The menu lists the agents with a
   * variable of the subject kind's type, and fills it with the subject's id.
   */
  subject: LaunchSubject;
}

/**
 * Click-to-open menu of the agents in the caller's org with a variable that
 * takes the subject's kind of record (``LAUNCH_SUBJECT_KINDS``). Selecting one
 * opens its launch page with the subject's id pre-filled on those variables.
 *
 * Render contract by query state:
 *
 * * ``isPending`` (initial load) or ``isError``: render nothing. The
 *   button is a progressive-enhancement affordance; we'd rather have
 *   it appear on resolve than show a skeleton or a disabled button
 *   that the user might click before the agents list is ready.
 * * loaded with no agent that takes the subject: render nothing. An
 *   unused button on a tab the user can already use to read existing
 *   threads adds visual noise without offering a path forward.
 * * loaded with qualifying agents: render the trigger in the
 *   threads-table toolbar.
 *
 * Visual + a11y modeled on :class:`CreateAgentMenu`: a
 * click-to-show panel in the foundation ``Popover``, which portals it
 * and keeps it in the viewport. No ``role="menu"`` / ``role="menuitem"``
 * markup because the panel does not implement arrow-key, Home/End,
 * type-ahead, or roving tabindex semantics — advertising the menu role
 * without those would lie to assistive tech.
 */
export function LaunchAgentMenu({ subject }: LaunchAgentMenuProps) {
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;
  // ``useAgents`` returns a single page. The backend route caps the
  // page at 100 (hardcoded in ``ai_router.py``'s ``list_agents`` — the
  // route doesn't expose ``limit`` as a query param), so an org with
  // more than 100 agents would silently truncate the dropdown. Lifting
  // the cap means paging on menu open (and likely exposing ``limit`` on
  // the route).
  const agentsQuery = useAgents({ orgId });
  const { variableType } = LAUNCH_SUBJECT_KINDS[subject.kind];

  const candidateAgents = React.useMemo<AgentRecord[]>(() => {
    if (!agentsQuery.data) {
      return [];
    }
    return agentsQuery.data.filter((agent) =>
      agent.variables.some((variable) => variable.type === variableType),
    );
  }, [agentsQuery.data, variableType]);

  const [open, setOpen] = React.useState(false);

  // Hide while the agents list is loading or errored as well as when
  // the loaded list has no agent that takes the subject — see the render
  // contract in the component docstring.
  if (
    agentsQuery.isPending ||
    agentsQuery.isError ||
    candidateAgents.length === 0
  ) {
    return null;
  }

  const handleSelect = (agent: AgentRecord) => {
    setOpen(false);
    void navigate(
      Goto.createAgentLaunchRoute(agent.agent_id, {
        [subject.kind]: subject.id,
      }),
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger render={<Button>Launch</Button>} />
      <Popover.Content
        align="end"
        aria-label={`Launch an agent on this ${subject.kind}`}
        className={styles.popover}
      >
        {/* The panel caps its height and clips what overflows, so the list
            scrolls inside it. */}
        <div className={styles.options}>
          {candidateAgents.map((agent) => (
            <button
              key={agent.agent_id}
              type="button"
              className={styles.option}
              onClick={() => handleSelect(agent)}
            >
              <span className={styles.optionLabel}>{agent.name}</span>
              {agent.description ? (
                <span className={styles.optionDescription}>
                  {agent.description}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </Popover.Content>
    </Popover>
  );
}
